/**
 * The CrestRewards HTTP layer.
 *
 * Every network call in every app goes through this module. No component and no
 * feature imports axios or calls fetch directly (CLAUDE.md convention 2).
 *
 * What it guarantees to callers:
 *   - Requests carry `Authorization: Bearer <token>` when a token is stored.
 *   - Successful responses resolve with the unwrapped `data` payload, never the
 *     `{ success, data, error }` envelope and never an AxiosResponse.
 *   - Failures reject with an ApiError carrying `.message`, `.status`, `.code`
 *     and `.details` — always safe to render.
 *   - A failure that ends the session — a 401, or a 403 `ACCOUNT_DEACTIVATED`
 *     from an account switched off mid-session — clears the stored token,
 *     records why in `SESSION_NOTICE_KEY`, and sends the user to /login, which
 *     shows that reason. See `endsSession` below for the exact rules.
 */
import axios from 'axios';

/** localStorage key for the Super Admin JWT. */
export const TOKEN_STORAGE_KEY = 'crestrewards.token';

/**
 * sessionStorage key carrying the reason a session ended, so the login page can
 * explain it after the redirect. sessionStorage rather than localStorage: the
 * message is about this tab's session and must not outlive it.
 */
export const SESSION_NOTICE_KEY = 'crestrewards.sessionNotice';

const DEFAULT_BASE_URL = 'http://localhost:4000/api';
const LOGIN_PATH = '/login';

/* -------------------------------------------------------------------------- */
/* Token storage                                                              */
/* -------------------------------------------------------------------------- */

/** Reads the stored token. Returns null in private-mode / storage-blocked browsers. */
export function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable — the session simply won't survive a reload.
  }
}

export function clearToken() {
  setToken(null);
}

/* -------------------------------------------------------------------------- */
/* Session notices                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Records why the session ended. Written just before the user is sent to
 * /login, so they arrive with an explanation instead of an empty form.
 */
export function setSessionNotice(message) {
  try {
    if (message) {
      window.sessionStorage.setItem(SESSION_NOTICE_KEY, message);
    } else {
      window.sessionStorage.removeItem(SESSION_NOTICE_KEY);
    }
  } catch {
    // Storage unavailable — the user still lands on /login, just without the reason.
  }
}

/**
 * Reads the notice and clears it in one go, so it shows exactly once.
 * Returns null when there is nothing to show.
 */
export function takeSessionNotice() {
  try {
    const message = window.sessionStorage.getItem(SESSION_NOTICE_KEY);
    if (message) window.sessionStorage.removeItem(SESSION_NOTICE_KEY);
    return message || null;
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The single error shape the UI ever sees. `message` is always a string that is
 * safe to put in front of a user.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, code = 'ERROR', details = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the request never reached the API (offline, CORS, server down). */
  get isNetworkError() {
    return this.status === 0;
  }
}

const FALLBACK_MESSAGES = {
  0: 'Cannot reach the CrestRewards API. Check that the backend is running.',
  304: 'The server returned a cached response with no content.',
  400: 'That request was not valid.',
  401: 'Your session has expired. Sign in again.',
  403: 'You do not have access to this resource.',
  404: 'That resource was not found.',
  409: 'That change conflicts with existing data.',
  422: 'Some fields need attention.',
  429: 'Too many requests. Wait a moment and try again.',
  500: 'Something went wrong on our side. Try again.',
  501: 'That is not available yet.',
};

function messageForStatus(status) {
  return FALLBACK_MESSAGES[status] ?? 'Something went wrong. Try again.';
}

/** Turns anything the API or the network hands back into an ApiError. */
function normaliseError(error) {
  // The server answered.
  if (error.response) {
    const { status, data } = error.response;
    const envelopeError = data && typeof data === 'object' ? data.error : null;

    return new ApiError(envelopeError?.message || messageForStatus(status), {
      status,
      code: envelopeError?.code ?? `HTTP_${status}`,
      details: envelopeError?.details ?? null,
    });
  }

  // The request was made but no response came back.
  if (error.code === 'ECONNABORTED') {
    return new ApiError('The request timed out. Try again.', {
      status: 0,
      code: 'TIMEOUT',
    });
  }

  return new ApiError(messageForStatus(0), { status: 0, code: 'NETWORK_ERROR' });
}

/* -------------------------------------------------------------------------- */
/* Client                                                                     */
/* -------------------------------------------------------------------------- */

export const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || DEFAULT_BASE_URL;

/**
 * An absolute URL for a file the API serves out of `/uploads`.
 *
 * The backend stores these paths host-relative (`/uploads/qr-batches/x.zip`)
 * because it has no idea what hostname it will be reached on. A browser then
 * resolves that against the page it is on — **the frontend dev server** — which
 * serves no `/uploads` route and answers with its own single-page app shell.
 * The result is a download link that renders the app's own "Page not found"
 * screen, which looks like a dead file rather than the wrong origin.
 *
 * So the origin has to come from the API base, with the `/api` prefix stripped:
 * `/uploads` is mounted beside `/api`, not underneath it.
 *
 * Anything already absolute is returned untouched, so a future move to S3 or a
 * CDN needs no change at the call sites.
 */
export function fileUrl(storedPath) {
  const p = String(storedPath ?? '');
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;

  const origin = API_BASE_URL.replace(/\/+$/, '').replace(/\/api$/, '');
  return origin + (p.startsWith('/') ? p : `/${p}`);
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
  /**
   * Required as of SA-7. The refresh token lives in an httpOnly cookie that the
   * browser only attaches to cross-origin requests when credentials are opted
   * into on BOTH sides — this flag here, and `credentials: true` on the API's
   * CORS config. Without it, `/auth/refresh` is called with no cookie and every
   * session dies silently at the 15-minute mark.
   */
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* -------------------------------------------------------------------------- */
/* Silent refresh                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The auth namespace this app's session lives under.
 *
 * Two apps share this client and they do NOT share an auth surface: the
 * console authenticates at `/superadmin/auth/*` and the tenant app at
 * `/client/auth/*`. This was hardcoded to the console until CA-1, which was
 * harmless only for as long as no Client Admin could log in — the moment one
 * could, every silent refresh would have posted to the console's endpoint,
 * 401'd, and ended the session at the 15-minute mark with nothing on screen to
 * explain it.
 *
 * Set it once, before the first request, with `configureAuth()`. Both apps do
 * so in their entry point rather than relying on the default, so neither is
 * the one that "just works" and hides a missing call in the other.
 */
let authBasePath = '/superadmin/auth';

/**
 * Points the session machinery at an app's auth routes.
 *
 * @param {{ basePath: string }} options `basePath` is relative to the API base
 *   URL and carries no trailing slash — e.g. `'/client/auth'`.
 */
export function configureAuth({ basePath }) {
  if (typeof basePath !== 'string' || !basePath.startsWith('/')) {
    throw new Error("configureAuth() needs a basePath like '/client/auth'.");
  }
  authBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
}

/** Path (relative to baseURL) of the refresh endpoint. */
const refreshPath = () => `${authBasePath}/refresh`;

/**
 * Routes whose own 401 must never trigger a refresh attempt.
 *
 * `login` — a wrong password is a 401 and has nothing to do with an expired
 * session; retrying it through refresh would swallow the error the form needs
 * to show. `refresh` — refreshing a failed refresh is an infinite loop.
 */
const noRefreshPaths = () => [refreshPath(), `${authBasePath}/login`];

/**
 * The single in-flight refresh, or null.
 *
 * This is what stops a thundering herd. A dashboard mounting six widgets fires
 * six requests at once; when the access token has expired they all come back
 * 401 together. Without this, each would start its own refresh — and because
 * the backend ROTATES refresh tokens and treats a replayed one as a leak
 * (revoking the whole chain), six concurrent refreshes would look exactly like
 * a stolen token and sign the user out. So: the first 401 starts the refresh,
 * every other 401 waits on the same promise.
 */
let refreshInFlight = null;

function refreshSession() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = axios
    .post(
      `${API_BASE_URL}${refreshPath()}`,
      {},
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    )
    .then((response) => {
      const token = response?.data?.data?.token;
      if (!token) throw new Error('Refresh response carried no token.');
      setToken(token);
      return token;
    })
    .finally(() => {
      // Cleared regardless of outcome so a later 401 can try again rather than
      // resolving instantly against a stale settled promise.
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === LOGIN_PATH) return;
  window.location.assign(LOGIN_PATH);
}

/**
 * Does this failure mean the session is over?
 *
 * Two cases, both matched on the backend's own error codes:
 *
 *   401 — the token is expired, malformed, or the account was deleted
 *         (`TOKEN_EXPIRED`, `INVALID_TOKEN`, `SESSION_INVALID`, `NO_TOKEN`).
 *         Any unrecognised 401 counts too, since an unauthenticated request is
 *         not something the UI can recover from on its own.
 *
 *   403 — `ACCOUNT_DEACTIVATED` only. The backend re-checks the account on every
 *         request, so an admin deactivated mid-session gets this on their very
 *         next call. Every other 403 ("you may not touch this resource") is a
 *         normal, recoverable error and must NOT sign the user out.
 *
 * `INVALID_CREDENTIALS` is excluded: that is a failed sign-in attempt on the
 * login form, not the end of an established session.
 */
function endsSession(error) {
  if (error.status === 403) return error.code === 'ACCOUNT_DEACTIVATED';
  if (error.status !== 401) return false;
  return error.code !== 'INVALID_CREDENTIALS';
}

/**
 * Tears the session down and leaves the user an explanation.
 *
 * The token is always cleared and the reason always recorded — a stale token is
 * worthless whoever asked for it. Only the redirect is suppressed by
 * `skipAuthRedirect`, which callers such as the login form and the session
 * restore in useAuth use so they can handle the failure in place.
 */
function handleSessionEnd(error, config) {
  if (!endsSession(error)) return;

  clearToken();
  setSessionNotice(error.message);

  if (!config?.skipAuthRedirect) {
    redirectToLogin();
  }
}

apiClient.interceptors.response.use(
  (response) => {
    /**
     * Opt-out for responses that are not the JSON envelope — file downloads.
     *
     * Both progress files predicted this case long before it existed: *"a future
     * file-download endpoint under /api will fail unless it opts out"*. A CSV
     * download cannot return `{ success, data, error }`, because the browser
     * needs the file bytes; without this flag the check below would reject it as
     * MALFORMED_RESPONSE and the user would be told "the server sent an
     * unexpected response" while the server was working perfectly.
     *
     * Deliberately opt-**in**: a caller has to ask for the raw response, so a
     * genuinely malformed reply on a normal endpoint is still caught loudly.
     * The caller receives the whole AxiosResponse and reads `.data`/`.headers`
     * itself.
     */
    if (response.config?.rawResponse) return response;

    const body = response.data;

    // Every CrestRewards endpoint answers with { success, data, error }.
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success) {
        // `data: null` is legitimate; a missing key is not. Normalise both to
        // null so a caller never receives undefined from a successful call.
        return body.data ?? null;
      }

      // A 2xx carrying success:false shouldn't happen, but if it does the
      // session rules still apply rather than being silently skipped.
      const apiError = new ApiError(
        body.error?.message || messageForStatus(response.status),
        {
          status: response.status,
          code: body.error?.code ?? 'ERROR',
          details: body.error?.details ?? null,
        },
      );

      handleSessionEnd(apiError, response.config);
      return Promise.reject(apiError);
    }

    // Anything else is not a CrestRewards response: a 304 with an empty body, a
    // proxy's HTML error page, a truncated payload. Resolving with it would push
    // `undefined` into a caller that has every reason to expect real data, and
    // the failure would surface somewhere far from here. Fail loudly instead.
    const malformed = new ApiError(
      'The server sent an unexpected response. Try again.',
      {
        status: response.status,
        code: 'MALFORMED_RESPONSE',
        details: { received: typeof body, url: response.config?.url ?? null },
      },
    );

    handleSessionEnd(malformed, response.config);
    return Promise.reject(malformed);
  },
  async (error) => {
    const apiError = normaliseError(error);
    const config = error.config;

    /**
     * A 401 now means "the 15-minute access token expired", not "the session is
     * over" — the refresh cookie is probably still good. Try once, silently,
     * and replay the original request.
     *
     * Guarded four ways: only on 401, never on the login or refresh routes
     * themselves, never on a request already retried (`_retried` marks it), and
     * never for INVALID_CREDENTIALS, which is a failed sign-in rather than an
     * expired session. If the refresh fails, we fall through to the normal
     * session-end path below and the user goes to /login with an explanation.
     */
    const canAttemptRefresh =
      apiError.status === 401 &&
      apiError.code !== 'INVALID_CREDENTIALS' &&
      config &&
      !config._retried &&
      !noRefreshPaths().some((path) => (config.url ?? '').includes(path));

    if (canAttemptRefresh) {
      try {
        const token = await refreshSession();
        config._retried = true;
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
        return await apiClient.request(config);
      } catch {
        // Fall through — the session really is over.
      }
    }

    handleSessionEnd(apiError, config);
    return Promise.reject(apiError);
  },
);

/* -------------------------------------------------------------------------- */
/* Platform endpoints                                                         */
/* -------------------------------------------------------------------------- */

/**
 * GET /api/health — platform liveness, not owned by any feature.
 * Resolves with { status, service, environment, database, tenantContext, ... }.
 */
export function getHealth(config) {
  return apiClient.get('/health', { skipAuthRedirect: true, ...config });
}


/* -------------------------------------------------------------------------- */
/* The public client — CA-5                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A second axios instance for the PUBLIC redemption journey, and the reason it
 * has to be separate from `apiClient` above.
 *
 * PROJECT_SPEC_CLIENT.md §7 puts a customer on a phone, in a shop, with no
 * account and no session. Three behaviours the main client has are actively
 * wrong for them:
 *
 *   1. **The Authorization header.** There is no token, and if a stale one were
 *      in localStorage — a client_admin who used this browser — it would be
 *      attached to a public request for no reason.
 *   2. **Silent refresh on 401.** A public 401 is not an expired session.
 *   3. **Redirect to /login.** This is the sharp one. A customer who hits any
 *      session-ending status would be thrown onto the tenant admin's login
 *      screen, mid-redemption, with their cashback abandoned. There is no
 *      account for them to sign into.
 *
 * What it KEEPS is everything callers depend on: the same `{ success, data,
 * error }` unwrapping, so a success resolves with the payload, and the same
 * `ApiError` shape, so `.message` is always a string safe to render.
 *
 * It lives here rather than as a bare `fetch` in the feature folder because
 * CLAUDE.md convention 3 says every API call goes through this package — and
 * because the envelope and error handling should have exactly one
 * implementation. A second one that drifted would show customers raw error
 * objects.
 *
 * `withCredentials` is deliberately FALSE: there is no cookie to send, and a
 * public endpoint should not be asking the browser for one.
 */
export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  // Longer than the console's 20s. The payout endpoint calls a payment partner
  // and then waits for it; a customer's request must not time out on our side
  // while money is in flight on theirs.
  timeout: 45000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

publicApiClient.interceptors.response.use(
  (response) => {
    const body = response.data;

    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success) return body.data ?? null;

      return Promise.reject(
        new ApiError(body.error?.message || messageForStatus(response.status), {
          status: response.status,
          code: body.error?.code ?? 'ERROR',
          details: body.error?.details ?? null,
        }),
      );
    }

    return Promise.reject(
      new ApiError('The server sent an unexpected response. Try again.', {
        status: response.status,
        code: 'MALFORMED_RESPONSE',
        details: { received: typeof body, url: response.config?.url ?? null },
      }),
    );
  },
  // No refresh, no redirect — just a renderable error. That is the whole point.
  (error) => Promise.reject(normaliseError(error)),
);

export default apiClient;
